import { faCompress, faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createContext, useContext, useState } from "react";

import { useLocale } from "../stores/locale";
import { Button } from "./button";

type FullscreenContextValue = {
  isFullscreen: boolean;
  setFullscreen: (value: boolean) => void;
  toggleFullscreen: () => void;
};

const FullscreenContext = createContext<FullscreenContextValue | undefined>(
  undefined,
);

/**
 * Wraps content that can be expanded to cover the whole viewport (including
 * navbar and side menu). Use `FullscreenToggle` or `useFullscreen` anywhere
 * inside to control it.
 */
export function Fullscreen({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [isFullscreen, setFullscreen] = useState(false);

  return (
    <FullscreenContext.Provider
      value={{
        isFullscreen,
        setFullscreen,
        toggleFullscreen: () => setFullscreen((value) => !value),
      }}
    >
      <div
        className={`${isFullscreen ? "fixed inset-0 z-50 overflow-auto overscroll-contain bg-white p-5" : ""} ${className ?? ""}`}
      >
        {children}
      </div>
    </FullscreenContext.Provider>
  );
}

/** Returns the closest `Fullscreen` context, or undefined when outside one */
export function useFullscreen(): FullscreenContextValue | undefined {
  return useContext(FullscreenContext);
}

/** Button toggling the closest `Fullscreen`. Renders nothing outside one. */
export function FullscreenToggle({ className }: { className?: string }) {
  const { locale: t } = useLocale();
  const fullscreen = useFullscreen();

  if (!fullscreen) {
    return null;
  }

  const title = fullscreen.isFullscreen
    ? t.fullscreen.exit
    : t.fullscreen.enter;

  return (
    <Button
      className={className}
      severity="secondary"
      title={title}
      onClick={fullscreen.toggleFullscreen}
    >
      <FontAwesomeIcon icon={fullscreen.isFullscreen ? faCompress : faExpand} />
    </Button>
  );
}
